import { Text, TextAreaInput } from 'common-components'
import { Field as FieldStyled } from '../../styled'
import { useState, useMemo, useEffect, useCallback } from 'react'
import { useWebWoodContext } from 'context/WebWoodContext'
import { ITextData } from 'types/media'
import { ECanvasObjectType } from '@renderforest/webwood-engine'
import debounce from 'lodash.debounce'
import { T } from 'consts/translationKeys'
import { translate } from 'utils/translations'

const TEXT_AREA_MIN_HEIGHT = 140
const TEXT_AREA_MAX_HEIGHT = 140

interface ITextInputProps {
  text: string
  persistTextData: (
    patch: Partial<ITextData>,
    sizeOverrides?: { width: number; height: number }
  ) => void
  mediaItemId: string
  onDelete: () => void
  id: string
}

export const TextInput = (props: ITextInputProps) => {
  const { text, persistTextData, mediaItemId, onDelete } = props
  const { updateObjectById, getMediaConfig } = useWebWoodContext()

  const [localText, setLocalText] = useState(text)

  const debouncedPersist = useMemo(
    () =>
      debounce((value: string) => {
        const config = getMediaConfig(ECanvasObjectType.text, mediaItemId)
        if (!config) return
        const sizeOverrides = { width: config.width, height: config.height }
        persistTextData({ text: value }, sizeOverrides)
      }, 300),
    [persistTextData, mediaItemId, getMediaConfig]
  )

  useEffect(() => {
    // Commit any pending text when this input goes away (the layer is
    // deselected / another item is selected before the 300ms debounce fires).
    // Cancelling here would drop the edit: the engine already has the new text
    // from the live `updateObjectById` above, so Redux would keep the stale
    // text and the two diverge on reselect. `flush()` is a no-op when nothing
    // is pending, and the debounce is bound to this item's id/persist fn, so it
    // always writes back to the correct item.
    return () => debouncedPersist.flush()
  }, [debouncedPersist])

  useEffect(() => {
    setLocalText(text)
  }, [text])

  const handleChange = useCallback(
    (value: string) => {
      setLocalText(value)
      updateObjectById(mediaItemId, {
        metadata: { text: value }
      } as Record<string, unknown>)
      if (value.trim()) {
        debouncedPersist(value)
      } else {
        debouncedPersist.cancel()
      }
    },
    [updateObjectById, mediaItemId, debouncedPersist]
  )

  const handleBlur = useCallback(() => {
    if (!localText.trim()) {
      debouncedPersist.cancel()
      onDelete()
      return
    }
    // Commit the latest text immediately instead of waiting out the debounce —
    // a deselect right after typing would otherwise unmount the overlay before
    // the pending persist fires, leaving Redux with the stale text.
    debouncedPersist.flush()
  }, [localText, debouncedPersist, onDelete])

  return (
    <FieldStyled>
      <Text type="subtitle">{translate(T.TEXT_BODY_CONTENT_LABEL)}</Text>
      <TextAreaInput
        dataTestId="text-overlay-text-area-input"
        value={localText}
        onChange={handleChange}
        onBlur={handleBlur}
        minHeight={TEXT_AREA_MIN_HEIGHT}
        maxHeight={TEXT_AREA_MAX_HEIGHT}
        isHandleOnPaste={false}
      />
    </FieldStyled>
  )
}
